import React from "react"
import { Legend, PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from "recharts"

import type { GetBioacousticsSampleDataQuery } from "@/__generated__/graphql"
import { myhiveLocalization, MyHiveLocalizationKey } from '@/localization/my-hive.js'
import { LanguageContext } from '@/providers/LanguageProvider.js'

import MiniSpinner from '../../asset/components/mini-spinner.js'

type BioacousticsData = GetBioacousticsSampleDataQuery['getBioacousticsSampleData'];

const HOURS = Array.from({ length: 24 }, (_, i) => i)

const SMOOTH_RADIUS = 2

function smooth(raw: number[]): number[] {
  const window = SMOOTH_RADIUS * 2 + 1
  return HOURS.map(h => {
    let sum = 0
    for (let d = -SMOOTH_RADIUS; d <= SMOOTH_RADIUS; d++) {
      sum += raw[(h + d + 24) % 24] ?? 0
    }
    return sum / window
  })
}

function buildChartData(data: BioacousticsData): Array<{ hour: string; pollinators: number; birds: number; pollinatorsRaw: number; birdsRaw: number }> {
  const rawPollinators = new Array<number>(24).fill(0)
  for (const obs of data.hourlyPollinatorObservations) {
    rawPollinators[obs.hour] = (rawPollinators[obs.hour] ?? 0) + obs.count
  }

  const rawBirds = new Array<number>(24).fill(0)
  for (const obs of data.birdObservations) {
    // dateTime is formatted in site-local timezone by the backend (formatInTimeZone),
    // so the HH portion of the ISO string is the local hour — don't parse via Date/getUTCHours.
    const match = String(obs.dateTime).match(/T(\d{2}):/)
    const h = match?.[1] !== undefined ? parseInt(match[1], 10) : NaN
    if (!isNaN(h)) rawBirds[h] = (rawBirds[h] ?? 0) + 1
  }

  const normalize = (values: number[]): number[] => {
    const max = Math.max(...values)
    return max === 0 ? values : values.map(v => v / max)
  }

  const smoothedPollinators = smooth(rawPollinators)
  const smoothedBirds = smooth(rawBirds)
  const normPollinators = normalize(smoothedPollinators)
  const normBirds = normalize(smoothedBirds)

  return HOURS.map(h => ({
    hour: h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`,
    pollinators: normPollinators[h] ?? 0,
    birds: normBirds[h] ?? 0,
    pollinatorsRaw: smoothedPollinators[h] ?? 0,
    birdsRaw: smoothedBirds[h] ?? 0,
  }))
}

function computePeakWindow(values: number[], threshold = 0.9): { start: string; end: string } | null {
  const max = Math.max(...values)
  if (max === 0) return null
  const norm = values.map(v => v / max)
  const peakIdx = values.indexOf(max)

  const fmtMins = (totalMins: number): string => {
    const h = Math.floor(totalMins / 60) % 24
    const m = Math.round(totalMins % 60)
    const period = h < 12 ? 'AM' : 'PM'
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h
    return `${displayH}:${String(m).padStart(2, '0')} ${period}`
  }

  // Rising edge: walk backward from peak to find where it crosses threshold
  let startMins = peakIdx * 60
  for (let i = 1; i <= 24; i++) {
    const h = (peakIdx - i + 24) % 24
    if ((norm[h] ?? 0) < threshold) {
      const vH = norm[h] ?? 0
      const vNext = norm[(h + 1) % 24] ?? 0
      const slope = vNext - vH
      const frac = slope !== 0 ? (threshold - vH) / slope : 0
      startMins = (h + frac) * 60
      break
    }
  }

  // Falling edge: walk forward from peak to find where it drops below threshold
  let endMins = peakIdx * 60
  for (let i = 1; i <= 24; i++) {
    const h = (peakIdx + i) % 24
    if ((norm[h] ?? 0) < threshold) {
      const vPrev = norm[(h - 1 + 24) % 24] ?? 0
      const vH = norm[h] ?? 0
      const slope = vH - vPrev
      const frac = slope !== 0 ? (threshold - vPrev) / slope : 0
      endMins = ((h - 1 + 24) % 24 + frac) * 60
      break
    }
  }

  return { start: fmtMins(startMins), end: fmtMins(endMins) }
}



export type RadarSeries = 'pollinators' | 'birds' | 'both';

const CHART_MARGIN = { top: 22, right: 36, bottom: 22, left: 36 }

export function BioacousticsHourlyRadarChart(params: { data?: BioacousticsData; series?: RadarSeries; showPeakLabel?: boolean }): React.JSX.Element {
  const { data, series = 'both', showPeakLabel = false } = params
  const { language } = React.useContext(LanguageContext)
  const t = myhiveLocalization[language]
  const [dims, setDims] = React.useState({ w: 0, h: 0 })
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      const rect = entries[0]?.contentRect
      if (rect) setDims({ w: rect.width, h: rect.height })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [data])

  const peakWindow = React.useMemo(() => {
    if (!showPeakLabel || !data) return null
    const cd = buildChartData(data)
    const values = series === 'birds' ? cd.map(d => d.birdsRaw) : cd.map(d => d.pollinatorsRaw)
    return computePeakWindow(values)
  }, [data, series, showPeakLabel])

  if (!data) {
    return <div className="w-full h-full flex items-center justify-center"><MiniSpinner /></div>
  }

  const chartData = buildChartData(data)

  const plotW = dims.w - CHART_MARGIN.left - CHART_MARGIN.right
  const plotH = dims.h - CHART_MARGIN.top - CHART_MARGIN.bottom
  const cx = dims.w / 2
  const cy = dims.h / 2
  const outerRadius = Math.min(plotW, plotH) / 2
  const r = outerRadius
  const left = cx - r
  const right = cx + r

  return (
    <div className="w-full h-full flex flex-col">
      <div
        ref={containerRef}
        className="flex-1 relative min-h-0"
      >
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={chartData} outerRadius="100%" margin={CHART_MARGIN}>
            <PolarGrid />
            <PolarAngleAxis
              dataKey="hour"
              tick={(rawProps) => {
                const { x: _x, y: _y, cx: _cx, cy: _cy, payload, index } = rawProps as unknown as { x: string | number; y: string | number; cx: string | number; cy: string | number; payload: { value: string }; index: number };
                if (index % 2 !== 0) return <g />;
                const rx = Number(_x); const ry = Number(_y);
                const cx = Number(_cx); const cy = Number(_cy);
                const dx = rx - cx; const dy = ry - cy;
                const len = Math.sqrt(dx * dx + dy * dy);
                const EXTRA = 10;
                const x = cx + dx * (1 + EXTRA / len);
                const y = cy + dy * (1 + EXTRA / len);
                const val = payload.value;
                const gray = 'var(--color-brand-warm-gray)';
                const dark = 'var(--color-brand-teal-dark-500)';
                if (val === '12am') {
                  return <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={9} fontWeight="700" fill={dark} letterSpacing="2">{t[MyHiveLocalizationKey.BioacousticsChartMidnight]}</text>;
                }
                if (val === '12pm') {
                  return <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={9} fontWeight="700" fill={dark} letterSpacing="2">{t[MyHiveLocalizationKey.BioacousticsChartNoon]}</text>;
                }
                if (val === '6am' || val === '6pm') {
                  return (
                    <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight="700" fill={dark}>
                      {val === '6am' ? t[MyHiveLocalizationKey.BioacousticsChart6AM] : t[MyHiveLocalizationKey.BioacousticsChart6PM]}
                    </text>
                  );
                }
                return (
                  <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={10} fill={gray}>
                    {val}
                  </text>
                );
              }}
            />
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false} domain={[0, 1]} tickCount={3} />
            {series === 'both' && <Legend />}
            {outerRadius > 0 && (
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
                <path d={`M ${left} ${cy} A ${r} ${r} 0 0 1 ${right} ${cy}`}
                  fill="none" stroke="rgba(20,18,60,1)" strokeWidth={4} strokeLinecap="butt" />
                <path d={`M ${right} ${cy} A ${r} ${r} 0 0 1 ${left} ${cy}`}
                  fill="none" stroke="rgba(250,185,30,1)" strokeWidth={4} strokeLinecap="butt" />
              </svg>
            )}
            {(series === 'pollinators' || series === 'both') && (
              <Radar dataKey="pollinators" name="Pollinators" stroke="var(--color-brand-warm-orange-500)" fill="var(--color-brand-warm-orange-500)" fillOpacity={0.35} dot={false} activeDot={false} />
            )}
            {(series === 'birds' || series === 'both') && (
              <Radar dataKey="birds" name="Birds" stroke="var(--color-brand-teal-500)" fill="var(--color-brand-teal-500)" fillOpacity={0.35} dot={false} activeDot={false} />
            )}
          </RadarChart>
        </ResponsiveContainer>
      </div>
      {showPeakLabel && peakWindow && (
        <p className="shrink-0 pt-3 text-center text-sm italic text-gray-500">
          {(() => {
            const color = series === 'birds' ? 'var(--color-brand-teal-500)' : 'var(--color-brand-warm-orange-500)'
            const style = { color, fontFamily: 'monospace' }
            return (
              <>
                {series === 'birds' ? t[MyHiveLocalizationKey.BioacousticsSongPeaksBetween] : t[MyHiveLocalizationKey.BioacousticsPeakWindowBetween]}{' '}
                <span className="not-italic font-semibold" style={style}>{peakWindow.start}</span>
                {' '}{t[MyHiveLocalizationKey.BioacousticsAnd]}{' '}
                <span className="not-italic font-semibold" style={style}>{peakWindow.end}</span>
                {'.'}
              </>
            )
          })()}
        </p>
      )}
    </div>
  )
}
